import React, {useState, useEffect} from "react";
import "./Account.css";
import { useNavigate } from "react-router-dom";
import UserService from "../../services/UserService";
import AccountsService from "../../services/AccountsService";
import Loader from "../Loader/Loader";
import ProductsService from "../../services/ProductsService";
import InvoicesService from "../../services/InvoicesService.js";
import GenericError from "../Utilities/GenericError";
import PopUp from "../Utilities/PopUp";
import {Link} from "@mui/material";
import TextBox from "./components/TextBox/TextBox";
import CheckBox from "./components/CheckBox/CheckBox";
import {formatDate, gradientGetter, Products, SubscriptionStatus} from "./utils";
import {useLoading} from "../../context/LoadingContext";
import {useAuth} from "../../context/AuthContext";
import CardCarousel from "./components/CardCarousel/CardCarousel";
import {useAccount} from "../../context/AccountContext";
import usePaymentProvider from "../../services/PaymentProvider.ts";

import {Trans, useTranslation} from "react-i18next";
import PersonalDetails from "./components/PersonalDetails/PersonalDetails";
import EvergentErrors from "../../services/Evergent/EvergentErrors.ts";
import SubscriptionInformation from "./components/SubscriptionInformation/SubscriptionInformation";

function Account() {

    const paymentProvider = usePaymentProvider();
    let navigate = useNavigate();
    const { t } = useTranslation();
    //context
    let account = useAccount();
    const {isLoading, removeLoading, addLoading} = useLoading();
    const {loggedIn, authData, authUpdateUser} = useAuth();

    const [canUpgrade, setCanUpgrade] = useState(false);
    const [periodEndDate, setPeriodEndDate] = useState("");
    const [hasUnsubscribed, setHasUnsubscribed] = useState(false);
    const [hasUsedCoupon, setHasUsedCoupon] = useState(false);
    const [hasFreeProduct, setHasFreeProduct] = useState(false);
    const [addonEligible, setAddonEligible] = useState([]);

    //Phone number verification
    const [verificationPopup, setVerificationPopup] = useState(false);
    const [verificationErrorPopup, setVerificationErrorPopup] = useState(false);
    const [verificationSuccessPopup, setVerificationSuccessPopup] = useState(false);
    const [errorPopup, setErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [paymentProfileList, setPaymentProfileList] = useState([]);


    const [userData, setUserData] = useState({})

    //Marketing field handling
    const [marketingDisabled, setMarketingDisabled] = useState(false)
    const [acceptMarketingCampaigns, setAcceptMarketingCampaigns] = useState(false);
    const onAcceptMarketingChange = () => {
        setMarketingDisabled(true);
    let value = acceptMarketingCampaigns;
    setAcceptMarketingCampaigns(!value);
    AccountsService.patchUser({ acceptedMarketing: !value })
      .then((response) => {
          setMarketingDisabled(false);
      })
      .catch((error) => {
          setMarketingDisabled(false);
      });
    };

    const handleDownloadInvoicePDF = (invoiceNumber) => {
        addLoading("DOWNLOADINVOICE")
        InvoicesService.downloadPDF({ invoiceNumber: invoiceNumber })
            .then(async response => {
                if(response.ok)
                    return await response.bytes();
                else
                    throw await response.json();
            })
            .then((bytes) =>{
                console.log(bytes);
                let pdfBlob = new Blob([bytes], { type: "application/pdf" });
                let url = URL.createObjectURL(pdfBlob);
                window.open(url)
                }
            )
            .catch((error) => {
                console.log(error);
                setErrorPopup(true);
                let errorCodeKey = error?.response?.data?.code;
                let translatedMessage = t(errorCodeKey+"");
                if(errorCodeKey && translatedMessage !== errorCodeKey+"") {
                    setErrorMessage(translatedMessage);
                }
                else {
                    setErrorMessage(t("301"));
                }
            })
            .finally(()=>{
                removeLoading("DOWNLOADINVOICE")
            });
    };

    useEffect(() => {
        if(!(isLoading("INIT"))){
            addLoading("INIT")
        }
        if(loggedIn){
            AccountsService.getProfile()
                .then((response) => {
                    const filteredCoupons = response.data.couponsInformation?.filter(
                        (item) => item.discount_type !== "amount"
                    );

                    setUserData({
                        ...response.data,
                        couponsInformation: filteredCoupons
                    });
                    account.setUserData({
                        ...response.data,
                        couponsInformation: filteredCoupons
                    });
                    updateFields({
                        ...response.data,
                        couponsInformation: filteredCoupons
                    })
                    setAcceptMarketingCampaigns(response.data.acceptedMarketing);

                    setPeriodEndDate(formatDate(response.data.subscription?.current_period_ends_at));

                    if(response.data.paymentProfileList?.length > 1 && !!response.data?.subscription?.credit_card?.id){
                        const temp = response.data.paymentProfileList?.findIndex(item => item.id === response.data?.subscription?.credit_card?.id)
                        setPaymentProfileList([response.data.paymentProfileList[temp], ...response.data.paymentProfileList?.slice(0, temp), ...response.data.paymentProfileList?.slice(temp + 1)])
                    }else{
                        setPaymentProfileList(response.data.paymentProfileList ?? [])
                    }

                    if (response.data.subscription) {
                        setHasUsedCoupon(response.data.subscription.coupon_code ? true : false);
                        ProductsService.getProductReferenceStandalone({
                            productId: response.data.subscription.product.id,
                            pricePoint: response.data.subscription.product.product_price_point_handle,
                        })
                            .then((res) => {
                                // setShowVoucher(res.data.displayName === "Village");
                                setAddonEligible(res.data?.addons)
                                setHasFreeProduct(!!res.data.downgrades && res.data.downgrades.length === 0)
                                setCanUpgrade((!!res.data.upgrades && res.data.upgrades.length > 0) || (!!res.data.downgrades && res.data.downgrades.length > 0));
                                const nextProductIdCheck =
                                    response.data.subscription.next_product_id !== undefined
                                        ? response.data.subscription.next_product_id.toString() ===
                                        process.env.REACT_APP_FREE_PRODUCT_ID.toString()
                                        : false;
                                setHasUnsubscribed(nextProductIdCheck);
                            })
                            .catch((e)=>{

                            })
                    }
                })
                .catch((e)=>{
                    setUserData(authData)
                    updateFields(authData)
                    setAcceptMarketingCampaigns(authData.acceptedMarketing);
                })
                .finally(()=>{
                    removeLoading("INIT")
                })
        }
    }, [loggedIn]); //eslint-disable-line react-hooks/exhaustive-deps

  const onChangePaymentInfo = (e) => {
    e.preventDefault();
    addLoading("ADDPAYMENTPROFILE")
    account.setChargifyAccess(true);
    account.setChangePayment(true);
    AccountsService.addPaymentProfile()
        .then(r => {
            window.addEventListener("message", function handleMessage(event){
                const iframeUrl = new URL(r.data.uri);
                if (!iframeUrl || (event.origin !== iframeUrl?.origin && event.origin !== window.location.origin)) {
                    return;
                }

                let data;
                try {
                    data = JSON.parse(event.data);
                }catch (e){
                    data = event.data;
                }

                if(data?.status !== undefined) {
                    console.log("Successful flow", parseInt(data.status))
                    window.removeEventListener("message", handleMessage);
                    if ((parseInt(data.status) === 1)) {
                        navigate("/Account");
                    }else if(parseInt(data.status) === 0){
                        if(data.errorCode === EvergentErrors.REQUEST_TERMINATED.value){
                            navigate("/Account");
                        }
                    }else if(parseInt(data.status) === 400){
                        window.removeEventListener("message", handleMessage);
                    }
                }
            })
            navigate(paymentProvider.paymentForm, {state : { uri: r.data.uri}});
        })
        .catch(e => {
            console.log("Error occured")
            setErrorPopup(true);
            let errorCodeKey = e?.response?.data?.code;
            let translatedMessage = t(errorCodeKey+"");
            if(errorCodeKey && translatedMessage !== errorCodeKey+"") {
                setErrorMessage(translatedMessage);
            }
            else {
                setErrorMessage(t("301"));
            }
        })
        .finally(()=>{
            removeLoading("ADDPAYMENTPROFILE")
        })
  };

  const sendVerificationEmail = () =>{
    addLoading("SENTEMAIlVER")
    AccountsService.actions({redirectUri: process.env.REACT_APP_VERIFY_EMAIL_REDIRECT_URI, action: "VERIFY_EMAIL"})
        .then(()=>{
          setVerificationErrorPopup(false);
          setVerificationSuccessPopup(true);
        })
        .catch((err)=>{
          setVerificationErrorPopup(true);
        })
        .finally(()=>{
          setVerificationPopup(false);
          removeLoading("SENTEMAIlVER")
        })
  }

    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false)

    //progress bar
    const [progressBarCompletion, setProgressBarCompletion] = useState("")
    const [persentCompletion, setPersentCompletion] = useState(13)

    useEffect(()=>{
        let progress =2;
        if(userData?.emailVerified){
            progress++;
        }
        if(userData?.firstName && userData?.firstName !== "Anonymous"){
            progress++;
        }
        if(userData?.lastName && userData?.lastName !== "User"){
            progress++;
        }
        if(userData?.dateOfBirth){
            progress++;
        }
        if(userData?.gender){
            progress++;
        }
        if(userData?.phoneNumber){
            progress++;
        }
        // if(acceptMarketingCampaigns){
        //     progress++;
        // }

        setProgressBarCompletion("complete"+progress)
        setPersentCompletion(Math.ceil(12.5*progress))
    }, [userData])



    /** Personal data state */
    const [formData, setFormData] = useState({
        firstName: {
            value: '',
            error: false
        },
        lastName: {
            value: '',
            error: false
        },
        dateOfBirth: {
            value: '',
            error: false
        },
        gender: {
            value: '',
            error: false
        },
        phoneNumber: {
            value: '',
            error: false
        }
    });

    const updateFields = (source) =>{
        setFormData(prev => {
            const diffs = {};
            Object.keys({...source, ...prev}).forEach(key => {
                if(prev.hasOwnProperty(key)){
                    if((key === "firstName" && source[key] === "Anonymous") ||
                        (key === "lastName" && source[key] === "User") ||
                        (source[key] === '' || source[key] === undefined || source[key] === null)){
                        diffs[key]= {value: '', error: false};
                        return;
                    }
                    diffs[key]= {value: source[key], error: false};
                }
            })
            return diffs;
        })
    }

    const [phoneNumberVerify, setPhoneNumberVerify] = useState(false);

    const updateUserData = async ()=>{
        return authUpdateUser(true).then((userDataResponse) => {
            AccountsService.getProfile()
                .then((updatedProfile) => {
                    setUserData(updatedProfile.data);
                })
                .catch((e)=>{
                    setUserData(userDataResponse)
                    updateFields(userDataResponse)
                })
                .finally(()=>{
                    removeLoading("INIT")
                })
        })
    }
    /** </Personal data state> **/
    return (
    <div>
      {isLoading() ? (
        <Loader />
      ) : (
          <div className={"account-wrapper"}>
              <div className={"account-header"}>
                  <span className={"account-title"}>
                      {t("myAccount")}
                  </span>
                  <div className={"account-progress-section"}>
                      <div className={"account-progress-section-top"}>
                          <span>{t("profileTitle")}</span>
                          <span>{persentCompletion}%</span>
                      </div>
                      {persentCompletion === 100 ?
                          <span className={"account-progress-section-middle"}>{t("filledProfilePrompt")}</span>
                          :
                          <span className={"account-progress-section-middle"}>
                              {t("pendingProfilePrompt")}
                          </span>
                      }
                      <div className={`account-progress-section-bot ${progressBarCompletion}`}></div>
                  </div>
              </div>
              <div className={"account-content"}>
                  <div className={"account-data"}>
                      <div className={"account-connect-data"}>
                          <span className={"account-connect-data-title"}>{t("accountLoginDetails")}</span>
                          <div className={"account-connect-data-wrapper"}>
                              <div className={"account-textboxes-grouped"}>
                                  <TextBox fieldTitle={t("accountEmailField")}
                                           defaultValue={userData?.email} redirectEdit={()=> UserService.updateProfile()}
                                           hasIcon={userData?.emailVerified ? "images/emailVerified.png" : "images/emailNeedsAction.png"}
                                           iconAlt={userData?.emailVerified ? "email is verified" : "email needs to be verified"}
                                           iconOnClick={!(userData?.emailVerified) ? ()=>{
                                               setVerificationPopup(true);
                                               setVerificationErrorPopup(false);
                                           } : undefined}/>
                                  <TextBox fieldTitle={t("accountPasswordField")} defaultValue={"************"} titleInfo={<div className={"account-code-tooltip-elements"}>
                                      <span>{t("accountPasswordInfoTitle")}</span>
                                      <ul>
                                          <li>{t("accountPasswordInfoContent1")}</li>
                                          <li>{t("accountPasswordInfoContent2")}</li>
                                          <li>{t("accountPasswordInfoContent3")}</li>
                                          <li>{t("accountPasswordInfoContent4")}</li>
                                      </ul>
                                  </div>} type={"password"} redirectEdit={()=>UserService.updatePassword()} />
                              </div>
                              <div className={"account-ads-checkbox"}>
                                  <CheckBox disabled={marketingDisabled} action={onAcceptMarketingChange} checked={acceptMarketingCampaigns} text={t("accountAgreeMarketing")}/>
                              </div>
                          </div>
                      </div>

                      <PersonalDetails formData={formData} setFormData={setFormData}
                                       userData={userData} setUserData={setUserData} updateFields={updateFields}
                                       setPhoneNumberVerify={setPhoneNumberVerify} phoneNumberVerify={phoneNumberVerify}
                                       updateUserData={updateUserData}/>
                  </div>

                  <SubscriptionInformation userData={userData}
                                           canUpgrade={canUpgrade} periodEndDate={periodEndDate}
                                           hasUnsubscribed={hasUnsubscribed} hasUsedCoupon={hasUsedCoupon}
                                           hasFreeProduct={hasFreeProduct} addonEligible={addonEligible}
                                           updateUserData={updateUserData} setErrorPopup={(errorMessageCode= "301")=>{setErrorMessage(t(errorMessageCode));setErrorPopup(true)}}/>

                  {userData?.subscription?.status === SubscriptionStatus.ActiveCancelled || !!paymentProfileList?.length ?
                      <div className={"card-section"}>
                      <span className={"card-section-header"}>{t("accountPaymentInfo")}</span>
                      <div className={"card-section-data"}>
                          <div className={"card-section-data-data"}>
                              <div className={"card-title"}>
                                  <span>{t("accountPaymentMyCards")}</span>
                                  <button disabled={paymentProfileList.length > 2} data-label={t("accountPaymentAddDesktop")} className={"card-title-button"} onClick={onChangePaymentInfo}>{t("accountPaymentAdd")}</button>
                              </div>
                              {!!paymentProfileList?.length ? <>
                                  <CardCarousel list={paymentProfileList} hasActiveCard={userData.subscription?.credit_card?.id === paymentProfileList[0].id}/>
                              </>:
                                  <span className={"card-section-data-empty"}>{t("noStoredCards")}</span>
                              }
                          </div>
                          {userData?.paymentsHistory?.filter(it => it.product_name !== Products.FREE && it.status === "paid").length ?
                              <div className={"card-section-history"}>
                                  <div className={"card-section-history-wrapper"}>
                                      <span className={"card-section-history-header"}>{t("accountPaymentHistoryTitle")}</span>
                                      <div className={"card-section-history-data"}>
                                          {userData?.paymentsHistory?.filter(it => it.product_name !== Products.FREE && it.status === "paid").slice(...(isHistoryExpanded ? [0] : [0, 3])).map((it, index) => {
                                              return (<div className={"card-section-history-item-wrapper"} key={index}>
                                                  <div className={`card-section-history-item ${gradientGetter(it.product_name)}`}>
                                                      <div className={"card-section-history-item-data"}>
                                                          <div className={"card-section-history-item-two-group"}>
                                                              <div className={"card-section-history-item-field"}>
                                                                  <span
                                                                      className={"card-section-history-item-field-top"}>{t("accountPaymentHistoryRowTitle")}</span>
                                                                  <span
                                                                      className={"card-section-history-item-field-bot"}>{it.product_name}</span>
                                                              </div>
                                                              <div className={"card-section-history-item-field"}>
                                                                  <span className={"card-section-history-item-field-top"}>{t("accountPaymentHistoryRowAmmount")}</span>
                                                                  <span
                                                                      className={"card-section-history-item-field-bot"}>{it.paid_amount}€ </span>
                                                              </div>
                                                          </div>
                                                          <div className={"card-section-history-item-two-group"}>
                                                              <div className={"card-section-history-item-field"}>
                                                                  <span className={"card-section-history-item-field-top"}>{t("accountPaymentHistoryRowDate")}</span>
                                                                  <span
                                                                      className={"card-section-history-item-field-bot"}>{formatDate(it.paid_date)}</span>
                                                              </div>
                                                              <div className={"card-section-history-item-field"}>
                                                                  <span className={"card-section-history-item-field-top"}>{t("accountPaymentHistoryRowType")}</span>
                                                                  <span
                                                                      className={"card-section-history-item-field-bot"}>{t(`accountPaymentHistoryRowType${it?.paymentCategory}`, { defaultValue: t("accountPaymentHistoryRowTypeOther") })}</span>
                                                              </div>
                                                          </div>
                                                      </div>
                                                      <div className={"card-section-history-item-receipt"} onClick={() => handleDownloadInvoicePDF(it.number)}>
                                                         <img src={"images/account/downloadInvoice.svg"} alt={"view invoice"}/>
                                                         <span>{t("accountPaymentHistoryDownload")}</span>
                                                      </div>
                                                  </div>
                                              </div>)
                                          })}
                                      </div>
                                  </div>
                                  {userData?.paymentsHistory?.filter(it => it.product_name !== Products.FREE && it.status === "paid").length > 3 ?
                                      <>
                                          {isHistoryExpanded ?
                                              <span className={"card-section-history-expandMore"}
                                                    onClick={() => setIsHistoryExpanded(false)}>{t("accountPaymentHistoryLess")}</span> :

                                              <span className={"card-section-history-expandMore"}
                                                    onClick={() => setIsHistoryExpanded(true)}>{t("accountPaymentHistoryMore")}</span>

                                          }
                                      </>:<></>
                                  }
                              </div>: <></>
                          }
                      </div>
                  </div>:<></>
                  }
                  <div className={"disconnect-section"}>
                      <a href={"/devices"} className={"device-management-wrapper"}>
                          <span>{t("deviceManagmentCTA")}</span>
                          <img src={"images/account/brandArrow.svg"} alt={"redirect to devices page"}/>
                      </a>
                  </div>
              </div>
          </div>
      )}
        <PopUp
            openPopUp={verificationPopup}
            setOpenPopUp={setVerificationPopup}
            maxWidth="md"
            showCloseButton={true}
            outlined={false}
            error={false}
            closeWhenClickedOutside={true}
      >
        <GenericError
            setClose={setVerificationPopup}
            success={true}
            mainText={t("accountUnverfiedEmailPopupTitle")}
            buttonText={t("accountUnverifiedEmailPopupSendCTA")}
            showIcon={false}
            executeFunction={sendVerificationEmail}>
            <div className={"account-email-popup-secondary-wrapper"}>
                <span className={"account-email-popup-secondary-first"} >{t("accountUnverifiedEmailPopupText1")}</span>
            </div>
        </GenericError>
      </PopUp>
      <PopUp
          openPopUp={verificationErrorPopup}
          setOpenPopUp={setVerificationErrorPopup}
          maxWidth="md"
          showCloseButton={true}
          outlined={false}
          error={false}
          closeWhenClickedOutside={true}
      >
        <GenericError
            setClose={setVerificationErrorPopup}
            success={false}
            mainText={t("accountProblemSendingVerificationEmailTitle")}
            secondaryText={t("accountProblemSendingVerificationEmailContent")}
            hideButtons={true}
            showIcon={true}
        />
      </PopUp>
      <PopUp
          openPopUp={verificationSuccessPopup}
          setOpenPopUp={setVerificationSuccessPopup}
          maxWidth="md"
          showCloseButton={true}
          outlined={false}
          error={false}
          closeWhenClickedOutside={true}
      >
        <GenericError
            setClose={setVerificationSuccessPopup}
            success={true}
            mainText={t("accountEmailSentTitle")}
            hideButtons={true}
            showIcon={true}>
            <div className={"account-email-popup-secondary-wrapper"}>
                <span className={"account-email-popup-secondary-sub"} >{t("accountEmailSentContent")}</span>
            </div>
        </GenericError>
      </PopUp>
      <PopUp
            openPopUp={errorPopup}
            setOpenPopUp={setErrorPopup}
            showCloseButton={true}
            outlined={false}
            maxWidth="md"
            error={false}
            closeWhenClickedOutside={false}
      >
        <GenericError
            setClose={setErrorPopup}
            success={true}
            showIcon={false}>
            <span className={"pick-program-fallback-modal"}>
                <Trans i18nKey={errorMessage}
                        tOptions={{ interpolation: { escapeValue: false } }}
                        components={{
                            Link: <Link className="clickable" href={"https://help.antennaplus.gr/contact-us/"} target={"_blank"}/>
                        }}
                        
                />
            </span>
        </GenericError>
      </PopUp>
    </div>
  );
}

export default Account;